import * as yup from 'yup';
import { useContext, useEffect, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  makeStyles,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { ButtonLoading } from '@shared/components';
import { TFile } from '@modules/files';
import { TDocument } from '@modules/documents';
import { SignInContext, WorkflowPhase, useUsers } from '@modules/users';
import { TItemType, getCustomFieldValues } from '@modules/items';

import {
  ApprovalStatus,
  TApprovalPhaseKey,
  TApprovalUser,
  TUpdateApprovalDialogState,
  TViewApprovalDialogState,
} from '../types';
import { useUpdateApproval } from '../api';
import evalApprovalRules from '../helpers/evalApprovalRules.helper';
import resolveApprovalWorkflow from '../helpers/resolveApprovalWorkflow.helper';
import getEnabledPhases from '../helpers/getEnabledPhases.helper';
import CustomRuleErrors from './CustomRuleErrors';
import ApprovalPhaseUserField from './ApprovalPhaseUserField';
import {
  resolveInvalidFunctionNames,
  rowsFromApprovalUsers,
  serializeApprovalUserEntries,
} from '../helpers/functionRows.helper';
import useFunctionRows from '../hooks/useFunctionRows';
import getUpdateApprovalDefaultValues, { getSignedApprovalUserIds } from '../helpers/updateApprovalForm.helper';
import InvalidApprovalFunctionWarning from './InvalidApprovalFunctionWarning';

type TProps = {
  state?: TUpdateApprovalDialogState;
  setState: (state: TUpdateApprovalDialogState) => void;
  setViewApprovalDialogState: (state: TViewApprovalDialogState) => void;
  item: TDocument | TFile;
  itemType: TItemType;
};

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '820px',
  },
  dialogContent: {
    maxHeight: '70vh',
    overflowY: 'auto',
  },
});

const phaseArrayField = (phaseKey: TApprovalPhaseKey, label: string) =>
  yup
    .array()
    .of(yup.string().required())
    .when('$enabledPhases', {
      is: (phases: TApprovalPhaseKey[]) => phases.includes(phaseKey),
      then: (s) => s.required(`Select at least one ${label}.`).min(1, `At least one ${label} is required.`),
      otherwise: (s) => s.optional().default([]),
    });

const schema = yup.object({
  authors: phaseArrayField(ApprovalStatus.AUTHORING, 'author'),
  reviewers: phaseArrayField(ApprovalStatus.REVIEWING, 'reviewer'),
  approvers: phaseArrayField(ApprovalStatus.APPROVING, 'approver'),
});
type TFormData = yup.InferType<typeof schema>;

const UpdateApprovalDialog = ({ state, setState, setViewApprovalDialogState, item, itemType }: TProps) => {
  const { organization } = useContext(SignInContext);
  const { userMap } = useUsers();
  const isApprovalFunctionsEnabled = organization.featureSettings.approvalFunctions?.enabled;
  const approval = state?.approvalId ? item.approvals.find((a) => a._id === state.approvalId) : undefined;

  const isAuthoring = approval?.status === ApprovalStatus.AUTHORING;
  const isApproving = approval?.status === ApprovalStatus.APPROVING;
  const approvalEnabledPhases = useMemo(() => (approval ? getEnabledPhases(approval.phases) : []), [approval]);
  const hasAuthoring = approvalEnabledPhases.includes(ApprovalStatus.AUTHORING);
  const hasReviewing = approvalEnabledPhases.includes(ApprovalStatus.REVIEWING);
  const hasApproving = approvalEnabledPhases.includes(ApprovalStatus.APPROVING);

  // The status is always one of the approval's own phases, so authors are submitted exactly while AUTHORING.
  const submitsAuthors = isAuthoring;
  const submitsReviewers = !isApproving && hasReviewing;
  const submitsApprovers = hasApproving;
  const activeGroupIds = useMemo(
    () => new Set(organization.customGroups.map((g) => g.id)),
    [organization.customGroups]
  );

  const defaultValues: TFormData = getUpdateApprovalDefaultValues(approval);

  // --- Function rows (only used when feature ON) ---
  const initialAuthorRows = useMemo(
    () => (approval?.phases.AUTHORING ? rowsFromApprovalUsers(approval.phases.AUTHORING.users) : []),
    [approval]
  );
  const initialReviewerRows = useMemo(
    () => (approval?.phases.REVIEWING ? rowsFromApprovalUsers(approval.phases.REVIEWING.users) : []),
    [approval]
  );
  const initialApproverRows = useMemo(
    () => (approval?.phases.APPROVING ? rowsFromApprovalUsers(approval.phases.APPROVING.users) : []),
    [approval]
  );

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<TFormData>({
    resolver: yupResolver(schema),
    defaultValues,
    context: { enabledPhases: approvalEnabledPhases },
  });

  const [authorRows, setAuthorRows] = useFunctionRows(initialAuthorRows, (ids) =>
    setValue('authors', ids, { shouldValidate: true })
  );
  const [reviewerRows, setReviewerRows] = useFunctionRows(initialReviewerRows, (ids) =>
    setValue('reviewers', ids, { shouldValidate: true })
  );
  const [approverRows, setApproverRows] = useFunctionRows(initialApproverRows, (ids) =>
    setValue('approvers', ids, { shouldValidate: true })
  );

  const signedAuthorIds = getSignedApprovalUserIds(approval?.phases.AUTHORING?.users);
  const signedReviewerIds = getSignedApprovalUserIds(approval?.phases.REVIEWING?.users);
  const signedApproverIds = getSignedApprovalUserIds(approval?.phases.APPROVING?.users);

  // Signed users keep their stored function assignments - the backend only validates PENDING users.
  const invalidFunctionNames = useMemo(() => {
    if (!isApprovalFunctionsEnabled || !userMap) return [];
    const signed = (users?: TApprovalUser[]) => new Set(getSignedApprovalUserIds(users));
    const submittedPhases = [
      ...(submitsAuthors ? [{ rows: authorRows, excludedUserIds: signed(approval?.phases.AUTHORING?.users) }] : []),
      ...(submitsReviewers ? [{ rows: reviewerRows, excludedUserIds: signed(approval?.phases.REVIEWING?.users) }] : []),
      ...(submitsApprovers ? [{ rows: approverRows, excludedUserIds: signed(approval?.phases.APPROVING?.users) }] : []),
    ];
    return resolveInvalidFunctionNames(submittedPhases, activeGroupIds, userMap);
  }, [
    isApprovalFunctionsEnabled,
    userMap,
    activeGroupIds,
    submitsAuthors,
    submitsReviewers,
    submitsApprovers,
    authorRows,
    reviewerRows,
    approverRows,
    approval,
  ]);

  // --- Custom-rules evaluation
  const selectedAuthors = watch('authors') ?? [];
  const selectedReviewers = watch('reviewers') ?? [];
  const selectedApprovers = watch('approvers') ?? [];
  const approvalVersion = item.versions.find((v) => v.id === approval?.versionId);
  const customFieldValues = useMemo(() => getCustomFieldValues(approvalVersion), [approvalVersion]);
  const workflow = useMemo(
    () => resolveApprovalWorkflow(organization.approvalWorkflows, customFieldValues),
    [organization.approvalWorkflows, customFieldValues]
  );
  const rules = workflow ? { minimumUsers: workflow.minimumUsers, uniqueUsers: workflow.uniqueUsers } : undefined;
  const ruleErrors = useMemo(
    () =>
      evalApprovalRules(
        rules,
        {
          [WorkflowPhase.AUTHORING]: selectedAuthors,
          [WorkflowPhase.REVIEWING]: selectedReviewers,
          [WorkflowPhase.APPROVING]: selectedApprovers,
        },
        customFieldValues
      ),
    [rules, selectedAuthors, selectedReviewers, selectedApprovers, customFieldValues]
  );

  useEffect(() => {
    if (state?.isOpen) {
      reset(defaultValues);
      if (isApprovalFunctionsEnabled) {
        setAuthorRows(initialAuthorRows);
        setReviewerRows(initialReviewerRows);
        setApproverRows(initialApproverRows);
      }
    }
  }, [state?.isOpen]);

  const updateApproval = useUpdateApproval();
  const onSubmit = (data: TFormData) => {
    if (!state || !approval) return;
    updateApproval.mutate(
      {
        id: item._id,
        itemType,
        approvalId: approval._id,
        authors: submitsAuthors
          ? serializeApprovalUserEntries(isApprovalFunctionsEnabled, authorRows, data.authors)
          : undefined,
        reviewers: submitsReviewers
          ? serializeApprovalUserEntries(isApprovalFunctionsEnabled, reviewerRows, data.reviewers)
          : undefined,
        approvers: submitsApprovers
          ? serializeApprovalUserEntries(isApprovalFunctionsEnabled, approverRows, data.approvers)
          : undefined,
      },
      {
        onSuccess: ({ approval: updatedApproval }) => {
          setState({ ...state, isOpen: false });
          setViewApprovalDialogState({ isOpen: true, approvalId: updatedApproval._id });
        },
      }
    );
  };

  const classes = useStyles();

  return (
    <Dialog
      open={Boolean(state?.isOpen && approval)}
      onOpenChange={(_, data) => {
        if (state && !data.open) {
          setState({ ...state, isOpen: false });
          if (approval) setViewApprovalDialogState({ isOpen: true, approvalId: approval._id });
        }
      }}
    >
      <DialogSurface className={isApprovalFunctionsEnabled ? classes.dialogSurface : ''}>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            Update approval request for v{approval?.versionId}
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <div className="flex flex-col space-y-3 mb-4 mt-2">
              {hasAuthoring && (
                <ApprovalPhaseUserField
                  phase={ApprovalStatus.AUTHORING}
                  functionsEnabled={isApprovalFunctionsEnabled}
                  control={control}
                  errors={errors}
                  rows={authorRows}
                  onRowsChange={setAuthorRows}
                  customGroups={organization.customGroups}
                  signedUserIds={signedAuthorIds}
                  readOnly={!isAuthoring}
                  required
                />
              )}

              {hasReviewing && (
                <ApprovalPhaseUserField
                  phase={ApprovalStatus.REVIEWING}
                  functionsEnabled={isApprovalFunctionsEnabled}
                  control={control}
                  errors={errors}
                  rows={reviewerRows}
                  onRowsChange={setReviewerRows}
                  customGroups={organization.customGroups}
                  signedUserIds={signedReviewerIds}
                  readOnly={isApproving}
                  required
                />
              )}

              {hasApproving && (
                <ApprovalPhaseUserField
                  phase={ApprovalStatus.APPROVING}
                  functionsEnabled={isApprovalFunctionsEnabled}
                  control={control}
                  errors={errors}
                  rows={approverRows}
                  onRowsChange={setApproverRows}
                  customGroups={organization.customGroups}
                  signedUserIds={signedApproverIds}
                  required
                />
              )}
              <CustomRuleErrors ruleErrors={ruleErrors} />

              <InvalidApprovalFunctionWarning userNames={invalidFunctionNames} />
            </div>
          </DialogContent>
          <DialogActions>
            <ButtonLoading
              appearance="primary"
              loading={updateApproval.isPending}
              disabled={!isValid || ruleErrors.length > 0 || invalidFunctionNames.length > 0}
              onClick={handleSubmit(onSubmit)}
            >
              Update
            </ButtonLoading>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Cancel</Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default UpdateApprovalDialog;
